import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Mail, ShoppingBag, TrendingUp, Check, X, Truck, Package } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const STATUS_CONFIG = {
  pending: { label: "Pending", color: "text-yellow-300/90 border-yellow-500/40" },
  confirmed: { label: "Confirmed", color: "text-blue-300/90 border-blue-500/40" },
  shipped: { label: "Shipped", color: "text-purple-300/90 border-purple-500/40" },
  delivered: { label: "Delivered", color: "text-emerald-300/90 border-emerald-500/40" },
  cancelled: { label: "Cancelled", color: "text-rose-300/90 border-rose-500/40" },
};

const NEXT_STATUS = {
  pending: "confirmed",
  confirmed: "shipped",
  shipped: "delivered",
};

const Stat = ({ label, value, icon: Icon }) => (
  <div className="p-6 border border-[color:var(--goldenelia-gold)]/25 bg-black flex items-center gap-4">
    <div className="w-12 h-12 shrink-0 flex items-center justify-center border border-[color:var(--goldenelia-gold)]/40 text-[color:var(--goldenelia-gold)]">
      <Icon size={20} strokeWidth={1.3} />
    </div>
    <div>
      <p className="font-display text-[9px] tracking-[0.4em] uppercase text-[color:var(--goldenelia-cream)]/55">{label}</p>
      <p className="font-serif-luxury text-2xl gold-gradient-text mt-1">{value}</p>
    </div>
  </div>
);

const Login = ({ onSuccess }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/login`, { email, password });
      toast.success("Welcome back.");
      onSuccess();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-black">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-sm p-10 border border-[color:var(--goldenelia-gold)]/30 bg-black/70"
      >
        <p className="font-display text-[10px] tracking-[0.5em] uppercase text-[color:var(--goldenelia-gold)] text-center mb-3">
          Golden Elia
        </p>
        <h1 className="font-serif-luxury text-3xl gold-gradient-text text-center mb-10">
          Admin Sign In
        </h1>

        <label className="block mb-5">
          <span className="font-display text-[9px] tracking-[0.4em] uppercase text-[color:var(--goldenelia-cream)]/55 mb-2 block">Email</span>
          <input
            data-testid="admin-login-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-3 bg-transparent border border-[color:var(--goldenelia-gold)]/35 text-[color:var(--goldenelia-cream)] font-body text-sm focus:outline-none focus:border-[color:var(--goldenelia-gold)]"
          />
        </label>

        <label className="block mb-8">
          <span className="font-display text-[9px] tracking-[0.4em] uppercase text-[color:var(--goldenelia-cream)]/55 mb-2 block">Password</span>
          <input
            data-testid="admin-login-password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-3 bg-transparent border border-[color:var(--goldenelia-gold)]/35 text-[color:var(--goldenelia-cream)] font-body text-sm focus:outline-none focus:border-[color:var(--goldenelia-gold)]"
          />
        </label>

        <button
          data-testid="admin-login-submit"
          type="submit"
          disabled={loading}
          className="btn-solid-gold w-full justify-center !py-3 disabled:opacity-60"
        >
          {loading ? "Signing in…" : "Sign In"}
        </button>
      </form>
    </div>
  );
};

const Dashboard = ({ onLogout }) => {
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [tab, setTab] = useState("orders");
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, o, n] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/orders`),
        axios.get(`${API}/admin/newsletter`),
      ]);
      setStats(s.data);
      setOrders(o.data.orders);
      setSubscribers(n.data.subscribers);
    } catch (err) {
      toast.error("Failed to load admin data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const advance = async (id, next) => {
    try {
      const { data } = await axios.patch(`${API}/admin/orders/${id}/status`, { status: next });
      setOrders((arr) => arr.map((o) => (o.id === id ? data : o)));
      toast.success(`Status → ${STATUS_CONFIG[next].label}`);
      fetchAll();
    } catch {
      toast.error("Update failed.");
    }
  };

  const cancel = async (id) => {
    if (!window.confirm("Cancel this order?")) return;
    try {
      const { data } = await axios.patch(`${API}/admin/orders/${id}/status`, { status: "cancelled" });
      setOrders((arr) => arr.map((o) => (o.id === id ? data : o)));
      toast.success("Order cancelled.");
      fetchAll();
    } catch {
      toast.error("Cancel failed.");
    }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-black text-[color:var(--goldenelia-cream)] px-6 sm:px-12 py-10">
      <header className="flex items-center justify-between mb-12 pb-6 border-b border-[color:var(--goldenelia-gold)]/20">
        <div>
          <p className="font-display text-[10px] tracking-[0.5em] uppercase text-[color:var(--goldenelia-gold)]">Golden Elia</p>
          <h1 className="font-serif-luxury text-3xl gold-gradient-text mt-1">Admin Dashboard</h1>
        </div>
        <div className="flex items-center gap-4">
          <a
            href={`${API}/admin/orders/export.csv`}
            data-testid="admin-export-csv"
            className="inline-flex items-center gap-2 font-display text-[10px] tracking-[0.35em] uppercase text-[color:var(--goldenelia-gold)]/85 hover:text-[color:var(--goldenelia-gold)] transition-colors"
            download
          >
            <Mail size={14} /> Export CSV
          </a>
          <button
            data-testid="admin-logout"
            onClick={onLogout}
            className="inline-flex items-center gap-2 font-display text-[10px] tracking-[0.35em] uppercase text-[color:var(--goldenelia-cream)]/65 hover:text-[color:var(--goldenelia-gold)] transition-colors"
          >
            <LogOut size={14} /> Logout
          </button>
        </div>
      </header>

      {/* Stats */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
        <Stat label="Total Orders" value={stats?.total_orders ?? "—"} icon={ShoppingBag} />
        <Stat label="Pending" value={stats?.pending_orders ?? "—"} icon={Package} />
        <Stat label="Delivered" value={stats?.delivered_orders ?? "—"} icon={Truck} />
        <Stat label="Revenue" value={stats ? `€${stats.total_revenue}` : "—"} icon={TrendingUp} />
      </section>

      {/* Tabs */}
      <div className="flex gap-8 border-b border-[color:var(--goldenelia-gold)]/20 mb-8">
        {[
          { id: "orders", label: `Orders (${orders.length})` },
          { id: "subscribers", label: `Newsletter (${subscribers.length})` },
          { id: "recipes", label: `Recipe Book` },
        ].map((t) => (
          <button
            key={t.id}
            data-testid={`admin-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`pb-3 font-display text-[10px] tracking-[0.4em] uppercase transition-colors ${
              tab === t.id
                ? "text-[color:var(--goldenelia-gold)] border-b-2 border-[color:var(--goldenelia-gold)]"
                : "text-[color:var(--goldenelia-cream)]/55 hover:text-[color:var(--goldenelia-cream)]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading && (
        <p className="text-center py-12 font-display text-[10px] tracking-[0.4em] uppercase text-[color:var(--goldenelia-cream)]/50">
          Loading…
        </p>
      )}

      {!loading && tab === "orders" && (
        <div className="space-y-3" data-testid="admin-orders-list">
          {orders.length === 0 && (
            <p className="text-center py-12 font-serif-luxury italic text-[color:var(--goldenelia-cream)]/55">
              No orders yet.
            </p>
          )}
          {orders.map((o) => {
            const cfg = STATUS_CONFIG[o.status] || STATUS_CONFIG.pending;
            const next = NEXT_STATUS[o.status];
            const isCart = o.order_kind === "cart";
            const total = isCart ? o.total_price : o.total_price;
            const headline = isCart
              ? `${(o.items || []).reduce((s, it) => s + (it.qty || 1), 0)} items · €${total}`
              : `${o.product_type === "premium" ? "Premium Box" : o.product_type === "mid" ? "Heritage Box" : "Essentials Basket"} × ${o.quantity}`;
            return (
              <article
                key={o.id}
                data-testid={`admin-order-${o.id}`}
                className="grid grid-cols-1 md:grid-cols-[1fr_1fr_140px_220px] gap-4 p-5 border border-[color:var(--goldenelia-gold)]/15 bg-black hover:border-[color:var(--goldenelia-gold)]/35 transition-colors"
              >
                <div>
                  <p className="font-display text-[10px] tracking-[0.3em] uppercase text-[color:var(--goldenelia-gold)]">
                    {headline}
                  </p>
                  <p className="font-serif-luxury text-lg text-[color:var(--goldenelia-cream)] mt-1">{o.full_name}</p>
                  <p className="font-body text-xs text-[color:var(--goldenelia-cream)]/55 mt-0.5">{o.phone}{o.email ? ` · ${o.email}` : ""}</p>

                  {isCart && o.items && o.items.length > 0 && (
                    <ul className="mt-2 space-y-1" data-testid={`admin-order-items-${o.id}`}>
                      {o.items.map((it, idx) => (
                        <li
                          key={idx}
                          className="font-body text-[11px] text-[color:var(--goldenelia-cream)]/70 flex items-baseline gap-2"
                        >
                          <span className="text-[color:var(--goldenelia-gold)]/75">×{it.qty}</span>
                          <span className="truncate">{it.product_name} <span className="text-[color:var(--goldenelia-cream)]/40">({it.size})</span></span>
                          <span className="text-[color:var(--goldenelia-cream)]/50 ml-auto whitespace-nowrap">€{(it.unit_price * it.qty).toFixed(2)}</span>
                        </li>
                      ))}
                      {o.free_bottle && (
                        <li className="font-body text-[11px] text-[color:var(--goldenelia-gold)]/85 flex items-baseline gap-2 pt-1 border-t border-[color:var(--goldenelia-gold)]/10 mt-1.5">
                          <span>🎁</span>
                          <span className="truncate">FREE · {o.free_bottle.product_name} <span className="text-[color:var(--goldenelia-cream)]/40">({o.free_bottle.size})</span></span>
                        </li>
                      )}
                    </ul>
                  )}

                  {!isCart && o.is_gift && (
                    <p className="font-body text-[11px] text-[color:var(--goldenelia-gold)]/80 mt-1.5">
                      🎁 Gift{o.gift_recipient_name ? ` → ${o.gift_recipient_name}` : ""}
                    </p>
                  )}

                  {o.notes && (
                    <p className="font-body italic text-[11px] text-[color:var(--goldenelia-cream)]/50 mt-2">
                      “{o.notes}”
                    </p>
                  )}
                </div>
                <div>
                  <p className="font-body text-xs text-[color:var(--goldenelia-cream)]/70">{o.address}</p>
                  <p className="font-body text-xs text-[color:var(--goldenelia-cream)]/55 mt-0.5">{o.city}{o.postal_code ? ` · ${o.postal_code}` : ""}</p>
                  <p className="font-display text-[10px] tracking-[0.3em] uppercase text-[color:var(--goldenelia-gold)]/75 mt-2">
                    €{total}
                  </p>
                  <p className="font-display text-[8px] tracking-[0.35em] uppercase text-[color:var(--goldenelia-cream)]/35 mt-1">
                    {isCart ? "Cart Order" : "Legacy Box"} · {o.created_at ? new Date(o.created_at).toLocaleString() : ""}
                  </p>
                </div>
                <div className="flex md:items-center">
                  <span
                    data-testid={`admin-order-status-${o.id}`}
                    className={`inline-block px-3 py-1 border text-[10px] tracking-[0.3em] uppercase font-display ${cfg.color}`}
                  >
                    {cfg.label}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2 items-center justify-end">
                  {next && o.status !== "cancelled" && (
                    <button
                      data-testid={`admin-order-advance-${o.id}`}
                      onClick={() => advance(o.id, next)}
                      className="inline-flex items-center gap-1.5 px-3 py-2 border border-[color:var(--goldenelia-gold)]/45 text-[10px] tracking-[0.25em] uppercase font-display text-[color:var(--goldenelia-gold)] hover:bg-[color:var(--goldenelia-gold)] hover:text-black transition-colors"
                    >
                      <Check size={12} /> {STATUS_CONFIG[next].label}
                    </button>
                  )}
                  {o.status !== "cancelled" && o.status !== "delivered" && (
                    <button
                      data-testid={`admin-order-cancel-${o.id}`}
                      onClick={() => cancel(o.id)}
                      className="inline-flex items-center gap-1.5 px-3 py-2 border border-rose-500/35 text-[10px] tracking-[0.25em] uppercase font-display text-rose-300/80 hover:bg-rose-500/15 transition-colors"
                    >
                      <X size={12} /> Cancel
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}

      {!loading && tab === "subscribers" && (
        <div className="space-y-2" data-testid="admin-subscribers-list">
          {subscribers.length === 0 && (
            <p className="text-center py-12 font-serif-luxury italic text-[color:var(--goldenelia-cream)]/55">
              No subscribers yet.
            </p>
          )}
          {subscribers.map((s) => (
            <div
              key={s.id || s.email}
              data-testid={`admin-subscriber-${s.email}`}
              className="flex items-center gap-4 px-5 py-4 border border-[color:var(--goldenelia-gold)]/15 bg-black"
            >
              <Mail size={16} className="text-[color:var(--goldenelia-gold)]/70" />
              <span className="font-body text-sm text-[color:var(--goldenelia-cream)]">{s.email}</span>
              {s.lang && (
                <span className="font-display text-[9px] tracking-[0.3em] uppercase text-[color:var(--goldenelia-gold)]/55 ml-auto">
                  {s.lang}
                </span>
              )}
              <span className="font-display text-[9px] tracking-[0.25em] uppercase text-[color:var(--goldenelia-cream)]/40 ml-4">
                {s.created_at ? new Date(s.created_at).toLocaleDateString() : ""}
              </span>
            </div>
          ))}
        </div>
      )}

      {!loading && tab === "recipes" && <RecipeAdminPanel />}
    </div>
  );
};

const RecipeAdminPanel = () => {
  const [data, setData] = useState(null);
  const [access, setAccess] = useState(null);
  const [openRecipe, setOpenRecipe] = useState(null);
  const [grantEmail, setGrantEmail] = useState("");
  const [grantSource, setGrantSource] = useState("manual");
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/admin/recipes`),
        axios.get(`${API}/admin/recipe-access`),
      ]);
      setData(r1.data);
      setAccess(r2.data);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => { reload(); }, []);

  const grant = async (e) => {
    e.preventDefault();
    if (!grantEmail) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/recipe-access/grant`, { email: grantEmail, source: grantSource });
      setGrantEmail("");
      await reload();
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (email) => {
    if (!window.confirm(`Revoke access for ${email}?`)) return;
    await axios.delete(`${API}/admin/recipe-access/${encodeURIComponent(email)}`);
    await reload();
  };

  if (!data) return <p className="font-display text-[10px] tracking-[0.4em] uppercase text-[color:var(--goldenelia-cream)]/50 text-center py-12">Loading library…</p>;

  return (
    <div data-testid="admin-recipes-panel" className="space-y-10">
      {/* Stats row */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MiniStat label="Recipes in library" value={data.total} />
        <MiniStat label="One-time price" value={`€${data.price_one_time}`} />
        <MiniStat label="Subscription" value={`€${data.price_subscription}/μήνα`} />
        <MiniStat label="Active access" value={access?.active?.length ?? 0} />
      </section>

      {/* Pending requests */}
      <section>
        <h3 className="font-display text-[10px] tracking-[0.45em] uppercase text-[color:var(--goldenelia-gold)] mb-3">
          Pending Requests ({access?.requests?.filter(r => r.status === "pending_payment").length ?? 0})
        </h3>
        {(access?.requests || []).filter(r => r.status === "pending_payment").length === 0 ? (
          <p className="font-body text-xs text-[color:var(--goldenelia-cream)]/50">— No pending requests —</p>
        ) : (
          <div className="space-y-2">
            {access.requests.filter(r => r.status === "pending_payment").map((r, i) => (
              <div key={i} className="p-3 border border-[color:var(--goldenelia-gold)]/20 flex flex-wrap items-center gap-3" data-testid={`recipe-request-${i}`}>
                <div className="flex-1 min-w-[200px]">
                  <p className="font-serif-luxury text-sm text-[color:var(--goldenelia-cream)]">{r.full_name}</p>
                  <p className="font-body text-xs text-[color:var(--goldenelia-cream)]/65">{r.email}{r.phone ? ` · ${r.phone}` : ""}</p>
                </div>
                <span className="font-display text-[9px] tracking-[0.3em] uppercase text-[color:var(--goldenelia-gold)]/85">
                  {r.plan === "one_time" ? `€${data.price_one_time} εφάπαξ` : `€${data.price_subscription}/μήνα`}
                </span>
                <button
                  onClick={() => { setGrantEmail(r.email); setGrantSource(r.plan === "subscription" ? "subscription" : "payment"); }}
                  className="btn-ghost-gold !py-1 !px-3 text-[9px]"
                >
                  Fill →
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Grant access */}
      <section className="p-4 border border-[color:var(--goldenelia-gold)]/25 bg-black/40">
        <h3 className="font-display text-[10px] tracking-[0.45em] uppercase text-[color:var(--goldenelia-gold)] mb-3">
          Grant Access
        </h3>
        <form onSubmit={grant} className="flex flex-wrap gap-2">
          <input
            type="email"
            value={grantEmail}
            onChange={(e) => setGrantEmail(e.target.value)}
            placeholder="customer@email.com"
            required
            data-testid="admin-grant-email"
            className="flex-1 min-w-[200px] bg-black border border-[color:var(--goldenelia-gold)]/40 px-3 py-2 text-sm text-[color:var(--goldenelia-cream)] focus:border-[color:var(--goldenelia-gold)] outline-none"
          />
          <select
            value={grantSource}
            onChange={(e) => setGrantSource(e.target.value)}
            data-testid="admin-grant-source"
            className="bg-black border border-[color:var(--goldenelia-gold)]/40 px-3 py-2 text-sm text-[color:var(--goldenelia-cream)] focus:border-[color:var(--goldenelia-gold)] outline-none"
          >
            <option value="payment">Lifetime €50</option>
            <option value="subscription">Subscription</option>
            <option value="manual">Manual / Comp</option>
          </select>
          <button type="submit" disabled={busy} data-testid="admin-grant-submit" className="btn-solid-gold !py-2 !px-4 text-[10px]">
            {busy ? "..." : "Grant"}
          </button>
        </form>
      </section>

      {/* Active access list */}
      <section>
        <h3 className="font-display text-[10px] tracking-[0.45em] uppercase text-[color:var(--goldenelia-gold)] mb-3">
          Active Access ({access?.active?.length ?? 0})
        </h3>
        {(access?.active || []).length === 0 ? (
          <p className="font-body text-xs text-[color:var(--goldenelia-cream)]/50">— No active subscribers yet —</p>
        ) : (
          <div className="space-y-1.5">
            {access.active.map((a, i) => (
              <div key={i} className="p-2.5 border border-[color:var(--goldenelia-gold)]/15 flex items-center gap-3 text-sm" data-testid={`recipe-access-${i}`}>
                <span className="font-body text-[color:var(--goldenelia-cream)] flex-1 truncate">{a.email}</span>
                <span className="font-display text-[9px] tracking-[0.3em] uppercase text-[color:var(--goldenelia-gold)]/65">
                  {a.source}
                </span>
                {a.active && (
                  <button
                    onClick={() => revoke(a.email)}
                    data-testid={`revoke-${a.email}`}
                    className="font-display text-[9px] tracking-[0.3em] uppercase text-[color:var(--goldenelia-cream)]/45 hover:text-red-400 transition-colors"
                  >
                    Revoke
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Library preview */}
      <section>
        <h3 className="font-display text-[10px] tracking-[0.45em] uppercase text-[color:var(--goldenelia-gold)] mb-3">
          Library — {data.total} recipes
        </h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {data.recipes.map((r) => (
            <button
              key={r.slug}
              onClick={() => setOpenRecipe(r)}
              data-testid={`admin-recipe-${r.slug}`}
              className="text-left p-4 border border-[color:var(--goldenelia-gold)]/20 bg-black/40 hover:border-[color:var(--goldenelia-gold)]/55 transition-colors"
            >
              <p className="font-display text-[9px] tracking-[0.3em] uppercase text-[color:var(--goldenelia-gold)] mb-1">
                {r.category} · {r.time}
              </p>
              <h4 className="font-serif-luxury text-base text-[color:var(--goldenelia-cream)] leading-tight">{r.title}</h4>
            </button>
          ))}
        </div>
      </section>

      {openRecipe && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/90 backdrop-blur" onClick={() => setOpenRecipe(null)}>
          <div onClick={(e) => e.stopPropagation()} className="max-w-2xl w-full bg-black border border-[color:var(--goldenelia-gold)]/40 p-6 max-h-[88vh] overflow-auto">
            <p className="font-display text-[10px] tracking-[0.4em] uppercase text-[color:var(--goldenelia-gold)] mb-1">
              {openRecipe.category} · {openRecipe.time} · {openRecipe.serves} άτομα
            </p>
            <h3 className="font-serif-luxury text-2xl gold-gradient-text mb-3">{openRecipe.title}</h3>
            {openRecipe.origin && (
              <div className="mb-4 p-3 border-l-2 border-[color:var(--goldenelia-gold)]/60 bg-[color:var(--goldenelia-gold)]/5">
                <p className="font-display text-[9px] tracking-[0.35em] uppercase text-[color:var(--goldenelia-gold)] mb-1">⛰ Καταγωγή</p>
                <p className="font-serif-luxury italic text-[13px] text-[color:var(--goldenelia-cream)]/85 leading-relaxed">{openRecipe.origin}</p>
              </div>
            )}
            <p className="font-display text-[10px] tracking-[0.35em] uppercase text-[color:var(--goldenelia-gold-bright)] mb-2">Υλικά</p>
            <ul className="space-y-1 font-body text-[14px] text-[color:var(--goldenelia-cream)]/85 mb-4">
              {openRecipe.ingredients.map((it, i) => <li key={i}>· {it}</li>)}
            </ul>
            <p className="font-display text-[10px] tracking-[0.35em] uppercase text-[color:var(--goldenelia-gold-bright)] mb-2">Εκτέλεση</p>
            <ol className="space-y-1.5 font-body text-[14px] text-[color:var(--goldenelia-cream)]/85">
              {openRecipe.steps.map((s, i) => <li key={i}>{i + 1}. {s}</li>)}
            </ol>
            {openRecipe.tip && (
              <div className="mt-5 p-3 border-l-2 border-[color:var(--goldenelia-gold)] bg-[color:var(--goldenelia-gold)]/5 font-serif-luxury italic text-[13px] text-[color:var(--goldenelia-cream)]/85">
                ✨ {openRecipe.tip}
              </div>
            )}
            <button onClick={() => setOpenRecipe(null)} className="mt-5 btn-ghost-gold !py-2 !px-4 text-[10px]">Κλείσιμο</button>
          </div>
        </div>
      )}
    </div>
  );
};

const MiniStat = ({ label, value }) => (
  <div className="p-3 border border-[color:var(--goldenelia-gold)]/20 bg-black/40 text-center">
    <p className="font-display text-[9px] tracking-[0.3em] uppercase text-[color:var(--goldenelia-cream)]/55">{label}</p>
    <p className="font-serif-luxury text-xl gold-gradient-text mt-1">{value}</p>
  </div>
);

export default function Admin() {
  const [authed, setAuthed] = useState(null);
  const navigate = useNavigate();

  const check = useCallback(async () => {
    try {
      await axios.get(`${API}/auth/me`);
      setAuthed(true);
    } catch {
      setAuthed(false);
    }
  }, []);

  useEffect(() => {
    check();
  }, [check]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {
      /* ignore */
    }
    setAuthed(false);
    navigate("/admin", { replace: true });
  };

  if (authed === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <p className="font-display text-[10px] tracking-[0.4em] uppercase text-[color:var(--goldenelia-gold)]/55">
          Loading…
        </p>
      </div>
    );
  }

  if (!authed) return <Login onSuccess={() => setAuthed(true)} />;
  return <Dashboard onLogout={logout} />;
}
